import { useEffect, useState } from "react";
import { api, apiErr } from "@/api";
import Shell from "@/components/Shell";
import { exhibitorNav } from "./ExhibitorDashboard";
import { Spinner, Overline } from "@/components/Brand";
import { toast } from "sonner";
import { Mail, Phone, Package, CornerDownRight, Send } from "lucide-react";

export default function ExhibitorEnquiries() {
  const [rows, setRows] = useState(null);
  const [replyFor, setReplyFor] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => { const { data } = await api.get("/exhibitor/enquiries"); setRows(data); };
  useEffect(() => { load(); }, []);

  const sendReply = async (e) => {
    if (!text.trim()) { toast.error("Write a reply first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/exhibitor/enquiries/${e.id}/reply`, { message: text });
      if (e.email) {
        toast.success(data.email_sent ? `Reply emailed to ${e.email}` : data.email_configured ? "Saved · email could not be delivered" : "Reply saved (email not configured)");
      } else {
        toast.success("Reply saved · no email on file");
      }
      setReplyFor(null); setText(""); load();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setBusy(false); }
  };

  return (
    <Shell nav={exhibitorNav}>
      <Overline>Digital Enquiries</Overline>
      <h1 className="font-display text-3xl gold-text mt-2">Catalogue Enquiries</h1>
      <p className="text-slate-400 text-sm mt-1">Enquiries from shared catalogue links become leads automatically. Reply to email the visitor back.</p>

      {!rows ? <Spinner /> : rows.length === 0 ? (
        <p className="text-slate-400 text-center py-16" data-testid="enquiries-empty">No enquiries yet.</p>
      ) : (
        <div className="space-y-3 mt-5">
          {rows.map((e) => (
            <div key={e.id} className="glass rounded-2xl p-4" data-testid={`enquiry-${e.id}`}>
              <div className="flex items-center justify-between">
                <div className="text-slate-100 font-medium">{e.name}</div>
                <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300">{e.status}</span>
              </div>
              <div className="text-xs text-slate-400 mt-1 flex flex-wrap gap-3">
                {e.phone && <span className="flex items-center gap-1"><Phone size={11} /> {e.phone}</span>}
                {e.email && <span className="flex items-center gap-1"><Mail size={11} /> {e.email}</span>}
                {e.product && <span className="flex items-center gap-1"><Package size={11} /> {e.product}</span>}
              </div>
              {e.message && <p className="text-sm text-slate-300 mt-2">{e.message}</p>}
              <div className="text-[11px] text-slate-500 mt-2">{e.catalogue_title}</div>

              {(e.replies || []).length > 0 && (
                <div className="mt-3 space-y-2 border-l-2 border-amber-500/20 pl-3">
                  {e.replies.map((r, i) => (
                    <div key={i} className="text-xs text-slate-300 flex gap-1.5" data-testid={`reply-${e.id}-${i}`}>
                      <CornerDownRight size={12} className="text-amber-400 mt-0.5 shrink-0" />
                      <span>{r.message} <span className="text-slate-500">— {r.by || "you"}</span></span>
                    </div>
                  ))}
                </div>
              )}

              {replyFor === e.id ? (
                <div className="mt-3 space-y-2">
                  <textarea value={text} onChange={(ev) => setText(ev.target.value)} placeholder={e.email ? `Reply to ${e.name} (sent to ${e.email})` : "Reply (no email on file — saved internally)"} rows={2} className="w-full bg-black/30 gold-border rounded-xl px-3 py-2.5 text-sm text-slate-100 focus:outline-none" data-testid={`reply-input-${e.id}`} />
                  <div className="flex gap-2">
                    <button disabled={busy} onClick={() => sendReply(e)} className="btn-gold px-4 py-2 rounded-lg text-sm flex items-center gap-1.5 disabled:opacity-60" data-testid={`reply-send-${e.id}`}><Send size={14} /> {busy ? "Sending…" : "Send"}</button>
                    <button onClick={() => { setReplyFor(null); setText(""); }} className="glass px-4 py-2 rounded-lg text-sm text-slate-300">Cancel</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => { setReplyFor(e.id); setText(""); }} className="mt-3 text-xs text-amber-300 underline" data-testid={`reply-open-${e.id}`}>Reply</button>
              )}
            </div>
          ))}
        </div>
      )}
    </Shell>
  );
}
